import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { TenantsService } from '../tenants.service';

export interface TenantFormDialogData {
  tenantId?: string;
}

@Component({
  selector: 'app-tenant-form-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
  template: `
    <div class="dialog-header">
      <h2 mat-dialog-title>
        <mat-icon>add_business</mat-icon>
        {{ isEditMode ? 'Edit Tenant' : 'Create New Tenant' }}
      </h2>
      <button mat-icon-button class="close-button" (click)="onCancel()" [disabled]="loading">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-dialog-content>
      <form [formGroup]="tenantForm" (ngSubmit)="onSubmit()" id="tenantForm">
        <div class="form-row">
          <mat-form-field appearance="outline">
            <mat-label>Tenant Name</mat-label>
            <input matInput formControlName="name" placeholder="Enter tenant name" required />
            <mat-icon matPrefix>business</mat-icon>
            <mat-error *ngIf="tenantForm.get('name')?.invalid">
              {{ getErrorMessage('name') }}
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Business Type</mat-label>
            <input matInput formControlName="business_type" placeholder="Enter business type (optional)" />
            <mat-icon matPrefix>work</mat-icon>
            <mat-hint>e.g., Retail, Restaurant, Healthcare</mat-hint>
            <mat-error *ngIf="tenantForm.get('business_type')?.invalid">
              {{ getErrorMessage('business_type') }}
            </mat-error>
          </mat-form-field>
        </div>

        <div class="form-row">
          <mat-form-field appearance="outline">
            <mat-label>Country</mat-label>
            <input matInput formControlName="country" placeholder="Enter country (optional)" />
            <mat-icon matPrefix>public</mat-icon>
            <mat-hint>Country where the tenant operates</mat-hint>
            <mat-error *ngIf="tenantForm.get('country')?.invalid">
              {{ getErrorMessage('country') }}
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Phone Number</mat-label>
            <input matInput formControlName="phone" placeholder="Enter phone number (optional)" />
            <mat-icon matPrefix>phone</mat-icon>
            <mat-hint>Contact phone number</mat-hint>
            <mat-error *ngIf="tenantForm.get('phone')?.invalid">
              {{ getErrorMessage('phone') }}
            </mat-error>
          </mat-form-field>
        </div>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Trial End Date</mat-label>
          <input matInput [matDatepicker]="picker" formControlName="trial_ends_at"
            placeholder="Select trial end date (optional)" />
          <mat-icon matPrefix>event</mat-icon>
          <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-datepicker #picker></mat-datepicker>
          <mat-hint>Date when the trial period ends</mat-hint>
        </mat-form-field>

        <div class="form-section">
          <mat-slide-toggle formControlName="is_active" color="accent">
            <strong>Active Tenant</strong>
            <small>Enable or disable this tenant</small>
          </mat-slide-toggle>
        </div>

        <div class="info-box">
          <mat-icon>info</mat-icon>
          <p>
            Inactive tenants will not be able to access the system. All associated users will
            lose access until the tenant is reactivated.
          </p>
        </div>
      </form>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()" [disabled]="loading">Cancel</button>
      <button mat-raised-button color="primary" form="tenantForm" type="submit"
        [disabled]="loading || tenantForm.invalid">
        <mat-spinner *ngIf="loading" diameter="20"></mat-spinner>
        <span *ngIf="!loading">{{ isEditMode ? 'Update' : 'Create' }} Tenant</span>
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    mat-dialog-content {
      min-width: 560px;
      padding-top: 8px;
    }

    .form-row {
      display: flex;
      gap: 16px;
    }

    .form-row mat-form-field {
      flex: 1;
    }

    .full-width {
      width: 100%;
    }

    .form-section {
      margin: 16px 0;
    }

    .form-section mat-slide-toggle {
      display: flex;
      flex-direction: column;
    }

    .form-section small {
      color: #666;
      font-size: 12px;
      margin-top: 2px;
    }

    .info-box {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      background: #e3f2fd;
      border-radius: 8px;
      padding: 12px;
      margin-top: 8px;
    }

    .info-box mat-icon {
      color: #1976d2;
      flex-shrink: 0;
    }

    .info-box p {
      margin: 0;
      font-size: 13px;
      color: #555;
    }

    mat-dialog-actions button mat-spinner {
      display: inline-block;
      margin-right: 8px;
    }

    @media (max-width: 640px) {
      mat-dialog-content {
        min-width: unset;
      }

      .form-row {
        flex-direction: column;
        gap: 0;
      }
    }
  `],
})
export class TenantFormDialogComponent {
  tenantForm: FormGroup;
  loading = false;
  isEditMode: boolean;
  tenantId: string | undefined;

  constructor(
    private fb: FormBuilder,
    private tenantsService: TenantsService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<TenantFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TenantFormDialogData,
  ) {
    this.tenantId = data?.tenantId;
    this.isEditMode = !!this.tenantId;

    this.tenantForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      business_type: ['', [Validators.maxLength(100)]],
      country: ['', [Validators.maxLength(100)]],
      phone: ['', [Validators.maxLength(20)]],
      trial_ends_at: [''],
      is_active: [true],
    });

    if (this.isEditMode) {
      this.loadTenant();
    }
  }

  loadTenant(): void {
    if (!this.tenantId) return;
    this.loading = true;
    this.tenantsService.getTenant(this.tenantId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const tenant = response.data;
          this.tenantForm.patchValue({
            name: tenant.name,
            business_type: tenant.business_type,
            country: tenant.country,
            phone: tenant.phone,
            trial_ends_at: tenant.trial_ends_at ? new Date(tenant.trial_ends_at) : null,
            is_active: tenant.is_active,
          });
        }
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Failed to load tenant details', 'Close', { duration: 3000 });
        this.loading = false;
        this.dialogRef.close(false);
      },
    });
  }

  onSubmit(): void {
    if (this.tenantForm.invalid) {
      this.tenantForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const formValue = { ...this.tenantForm.value };

    if (formValue.trial_ends_at) {
      formValue.trial_ends_at = new Date(formValue.trial_ends_at).toISOString();
    }

    const request$ = this.isEditMode && this.tenantId
      ? this.tenantsService.updateTenant(this.tenantId, formValue)
      : this.tenantsService.createTenant(formValue);

    request$.subscribe({
      next: (response) => {
        if (response.success) {
          this.snackBar.open(
            `Tenant ${this.isEditMode ? 'updated' : 'created'} successfully`,
            'Close',
            { duration: 3000 },
          );
          this.dialogRef.close(true);
        }
        this.loading = false;
      },
      error: (error) => {
        const errorMessage = error.error?.message ||
          `Failed to ${this.isEditMode ? 'update' : 'create'} tenant`;
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        this.loading = false;
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  getErrorMessage(fieldName: string): string {
    const control = this.tenantForm.get(fieldName);
    if (control?.hasError('required')) return 'This field is required';
    if (control?.hasError('maxlength')) {
      return `Maximum length is ${control.errors?.['maxlength'].requiredLength} characters`;
    }
    return '';
  }
}
